import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

export default function DoctorDetailModal({ doctor, onClose }) {
  const navigate = useNavigate();

  // Esc 關閉
  useEffect(() => {
    const fn = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  // ====== 與 Schedule.jsx 一致的週期工具 ======
  const weekdayMap = ["日", "一", "二", "三", "四", "五", "六"]; // 與排班頁一致
  function getWeekDates(baseDate = new Date()) {
    const d = new Date(baseDate);
    const day = d.getDay(); // 0=Sun, 1=Mon...
    const diffToMonday = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diffToMonday);
    return Array.from({ length: 7 }, (_, i) => {
      const cur = new Date(d);
      cur.setDate(d.getDate() + i);
      return cur;
    });
  }
  function toDateKey(dt) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const weekDates = useMemo(() => getWeekDates(new Date()), []);

  // ====== 撈取本週 roster ======
  // rosterMap: Map<dateKey, { morning: string[], afternoon: string[], evening: string[] }>
  const [rosterMap, setRosterMap] = useState(new Map());
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [errorRoster, setErrorRoster] = useState("");

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoadingRoster(true);
        setErrorRoster("");
        const from = toDateKey(weekDates[0]);
        const to = toDateKey(weekDates[6]);
        const { data, error } = await supabase
          .from("roster")
          .select("*")
          .gte("date", from)
          .lte("date", to)
          .order("date", { ascending: true });
        if (abort) return;
        if (error) throw error;
        const m = new Map();
        (data || []).forEach((r) =>
          m.set(r.date, {
            morning: Array.isArray(r.morning) ? r.morning : [],
            afternoon: Array.isArray(r.afternoon) ? r.afternoon : [],
            evening: Array.isArray(r.evening) ? r.evening : [],
          }),
        );
        setRosterMap(m);
      } catch (e) {
        console.error("[DoctorDetailModal] roster fetch error:", e);
        setErrorRoster("讀取失敗");
        setRosterMap(new Map());
      } finally {
        if (!abort) setLoadingRoster(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [weekDates]);

  // ====== 名稱對照與比對 ======
  // 你提供的「本名不含稱謂」→ 暱稱
  const KNOWN_ALIASES = useMemo(
    () => ({
      李啟鐘: ["李醫師"],
      李家豪: ["小李醫師"],
      莊舒安: ["莊醫師"],
      簡德源: ["簡醫師"],
      邱格浩: ["邱醫師"],
    }),
    [],
  );

  // 取得「本名（不含稱謂/括號註記/空白）」：例 '李啟鐘 醫師' / '李啟鐘醫師' / '李啟鐘（代診）' → '李啟鐘'
  const getCanonical = (s) => {
    return (
      String(s || "")
        .replace(/\u3000/g, " ")
        .replace(/\s+/g, "")
        // 去除各式括號註記
        .replace(/（[^）]*）|\([^)]*\)|\[.*?\]|【.*?】|『.*?』|「.*?」/g, "")
        // 去除常見頭銜（多數全域移除），但「醫師／醫生」改為**只移除字尾**
        .replace(/總?院長|副?院長|院長|主治醫師?|主任|院士|教授|講師|博士/g, "")
        .replace(/(醫師|醫生)$/g, "")
        .replace(/[．・·･\.-]/g, "")
        .trim()
    );
  };

  // 生成本名＋暱稱等變體（避免 roster 與卡片名稱格式差異）
  const targetNames = useMemo(() => {
    const raw = String(doctor?.name || "").trim();
    const canonical = getCanonical(raw);
    const aliases = KNOWN_ALIASES[canonical] || [];
    const variants = [
      raw, // 原始顯示名（可能含空白或稱謂）
      canonical, // 純本名
      `${canonical}醫師`,
      `${canonical}醫生`,
      ...aliases, // 你提供的暱稱
    ]
      .filter(Boolean)
      .map((t) => t.trim());
    return Array.from(new Set(variants));
  }, [doctor?.name, KNOWN_ALIASES]);

  // 產生「該醫師本週課表」資料列
  const doctorWeek = useMemo(() => {
    const rows = weekDates.map((d) => {
      const dateKey = toDateKey(d);
      const dayRoster = rosterMap.get(dateKey);

      const mark = (arr) =>
        Array.isArray(arr) &&
        arr.some((n) => {
          const src = String(n || "").trim();
          // 1) 直接相等（含暱稱）
          if (targetNames.includes(src)) return true;
          // 2) 忍受空白差異
          const compact = src.replace(/\s+/g, "");
          return targetNames.some((t) => compact === t.replace(/\s+/g, ""));
        })
          ? "●"
          : "";

      return {
        day: `週${weekdayMap[d.getDay()]}`,
        morning: dayRoster ? mark(dayRoster.morning) : "",
        afternoon: dayRoster ? mark(dayRoster.afternoon) : "",
        evening: dayRoster ? mark(dayRoster.evening) : "",
      };
    });
    // 保險排序（正常 weekDates 已是週一→週日）
    return rows;
  }, [weekDates, rosterMap, targetNames]);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-card">
        {/* 上方：左上角照片 + 名稱 */}
        <div className="modal-header">
          <img className="modal-photo" src={doctor.photo} alt={doctor.name} />
          <div className="modal-title">{doctor.name}</div>
          <div className="reservation-button-container">
            <button
              type="button"
              className="reservation-button"
              onClick={() => navigate("/schedule")}
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                padding: "6px 12px",
                cursor: "pointer",
              }}
            >
              我要掛號
            </button>
          </div>
        </div>

        <div className="modal-body">
          {/* 左側 */}
          <div className="modal-left">
            <section>
              <h3>醫師專治</h3>
              <ul className="dot-list">
                {(doctor.specialties || []).map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </section>
            <section>
              <h3>醫師學經歷</h3>
              <ul className="dash-list">
                {(doctor.education || []).map((e) => (
                  <li key={e}>{e}</li>
                ))}
                {(doctor.experience || []).map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </section>
          </div>

          {/* 右側：門診課表（抓本週 roster） */}
          <div className="modal-right">
            <h3>本週門診課表</h3>
            {loadingRoster ? (
              <div style={{ padding: "6px 0", color: "#64748b" }}>讀取中…</div>
            ) : null}
            {errorRoster ? (
              <div style={{ padding: "6px 0", color: "#991b1b" }}>
                {errorRoster}
              </div>
            ) : null}
            <table className="schedule">
              <thead>
                <tr>
                  <th>星期</th>
                  <th>早診</th>
                  <th>午診</th>
                  <th>晚診</th>
                </tr>
              </thead>
              <tbody>
                {doctorWeek.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.day}</td>
                    <td>{r.morning || "—"}</td>
                    <td>{r.afternoon || "—"}</td>
                    <td>{r.evening || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
              依「本週門診時刻表」即時同步
            </div>
          </div>
        </div>

        {/* 底部：返回 */}
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>
            返回
          </button>
        </div>
      </div>
      <button className="backdrop-close" aria-label="關閉" onClick={onClose} />
    </div>
  );
}
